import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from '@/components/ui/IconButton';
import placeholder_portrait_200x200 from "@/assets/imgs/placeholders/portrait_200x200.jpg";
import placeholder_cover_1600x700 from "@/assets/imgs/placeholders/cover_1600x700.jpg";
import SafeImg from '@/components/shared/SafeImg';
import IdolAvatar from '@/components/ui/IdolAvatar';
import IdolName from '@/components/ui/IdolName';
import SuspendedMark from '@/components/ui/SuspendedMark';
import { formatDate } from '@/shared/utils/formatDate';

/**
 * 全域偶像卡片組件
 * 用於訂閱頁面、我的偶像頁面和其他需要顯示偶像資訊的地方
 *
 * @param {Object} idol - 偶像資料（沿用 subscribed_* 命名，各頁面把 API 欄位映射進來；
 *   subscribed_idol_is_official 必填，showOnline 時 subscribed_idol_id 必填）
 * @param {boolean} showSubscriptionInfo - 是否顯示訂閱資訊
 * @param {boolean} showOnline - 頭像是否顯示在線綠點（探索偶像、我的訂閱；我的偶像、封鎖列表不顯示）
 * @param {Object} toolbar - 工具列設定 { onEdit: fn, onDelete: fn }
 * @param {boolean} suspended - 偶像已停權：封面、頭像、名稱灰階，封面蓋停權印章，不可點進偶像頁（工具列不受影響）
 */
const IdolCard = ({
    idol,
    showSubscriptionInfo = false,
    onCancelSubscription,
    onRenewSubscription,
    toolbar,
    suspended = false,
    showOnline = false
}) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const {
        subscribed_idol_id,
        subscribed_idol_name,
        subscribed_idol_is_official,
        subscribed_idol_avatar_url,
        subscribed_idol_cover_url,
        subscribed_idol_uuid,
        plan_duration,
        subscription_price,
        next_billing_date,
        expired_at,
        status,
        auto_renew,
        discount
    } = idol;

    // 判斷訂閱狀態
    const isExpired = status === 'expired';
    // subscription_price 是字串（如 "0.00"），要轉成數字判斷
    const isFree = parseFloat(subscription_price) === 0;
    const isCancelled = !auto_renew && status === 'active';
    const isActive = auto_renew && status === 'active';

    // 格式化金額
    const formatPrice = (price) => parseFloat(parseFloat(price).toFixed(2));

    // 計算原價（如果有折扣）
    const currentPrice = parseFloat(subscription_price);
    // discount 為數字，沒有折扣時是 0；不能寫成 discount && ...，否則 JSX 會把 0 渲染在金額後面
    const hasDiscount = discount > 0;
    const originalPrice = hasDiscount ? currentPrice / (1 - discount / 100) : 0;

    // 停權偶像不可點進偶像頁
    const isLinkable = subscribed_idol_uuid && !suspended;
    const suspendedClass = suspended ? 'idol-suspended' : '';

    // 處理點擊跳轉到偶像個人頁
    const handleHeaderClick = () => {
        if (isLinkable) {
            navigate(`/idol/${subscribed_idol_uuid}`);
        }
    };

    return (
        <div className="content-card idol-card bg-white rounded shadowA d-flex flex-column position-relative">
            {/* 工具列（編輯、刪除按鈕） */}
            {toolbar && (
                <div className="position-absolute d-flex gap-2" style={{ top: '10px', right: '10px', zIndex: 10 }}>
                    {toolbar.onEdit && (
                        <IconButton size="sm" className="bg-white shadow-sm" tooltip={t('edit')} onClick={(e) => { e.stopPropagation(); toolbar.onEdit(); }}>
                            <FontAwesomeIcon icon="fa-solid fa-pen" />
                        </IconButton>
                    )}
                    {toolbar.onDelete && (
                        <IconButton size="sm" className="bg-white shadow-sm" tooltip={t('delete')} onClick={(e) => { e.stopPropagation(); toolbar.onDelete(); }}>
                            <FontAwesomeIcon icon="fa-solid fa-trash" />
                        </IconButton>
                    )}
                    {toolbar.onUnblock && (
                        <button
                            className="btn btn-sm btn-dark rounded-pill"
                            onClick={(e) => { e.stopPropagation(); toolbar.onUnblock(); }}
                        >
                            {t('unblock')}
                        </button>
                    )}
                </div>
            )}
            {/* 頭像與背景圖 */}
            <div className="position-relative idol-card-header">
                {/* 背景圖 */}
                <div className={`position-absolute w-100 rounded-top top-0 overflow-hidden content-card-cover img-cover ${suspendedClass}`}>
                    <SafeImg
                        src={subscribed_idol_cover_url || placeholder_cover_1600x700}
                        alt="cover"
                    />
                </div>
                {/* 頭像 */}
                <div
                    className={`position-absolute start-50 translate-middle-x idol-card-avatar ${isLinkable ? 'cursor-p' : ''} ${suspendedClass}`}
                    onClick={handleHeaderClick}
                >
                    <IdolAvatar
                        size="lg"
                        src={subscribed_idol_avatar_url || placeholder_portrait_200x200}
                        idolId={showOnline ? subscribed_idol_id : undefined}
                        imgClassName="border border-white"
                        alt={subscribed_idol_name}
                    />
                </div>
                {/* 停權印章：放在頭像之後，才會疊在頭像上方；以整個 header（封面＋頭像）為範圍置中 */}
                {suspended && (
                    <div className="position-absolute w-100 h-100 top-0 d-flex align-items-center justify-content-center">
                        <SuspendedMark variant="stamp" />
                    </div>
                )}
            </div>

            {/* 偶像名稱 */}
            <div className="px-3">
                <div className={`d-flex justify-content-center my-3 ${suspendedClass}`}>
                    <div
                        className={`mw-100 min-w-0 ${isLinkable ? 'link-dark cursor-p' : ''}`}
                        onClick={handleHeaderClick}
                    >
                        <IdolName name={subscribed_idol_name} isOfficial={subscribed_idol_is_official} size="md" />
                    </div>
                </div>
            </div>

            {/* 訂閱資訊 */}
            {showSubscriptionInfo && (
                <div className="d-flex flex-column flex-grow-1 p-4 pt-0">
                    {/* 內容區域 - 自動佔滿剩餘空間 */}
                    <div className="flex-grow-1">
                        {/* 訂閱方案與價格 */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-primary fw-bold">
                                {i18n.language === 'en' && plan_duration > 1
                                    ? t('subscription_duration_plural', { duration: plan_duration })
                                    : t('subscription_duration', { duration: plan_duration })}
                            </span>
                            {isFree ? (
                                <span className="text-primary fw-bold">{t('free')}</span>
                            ) : (
                                <div >
                                    <span className="text-primary fw-bold">
                                        ₣{formatPrice(currentPrice)}
                                    </span>
                                    {hasDiscount && (
                                        <span className="text-decoration-line-through text-muted ms-2">
                                            ₣{formatPrice(originalPrice)}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* 到期日 */}
                        <div className="d-flex justify-content-between mb-2">
                            <span>{t('expiry_date')}</span>
                            <span>{formatDate(expired_at)}</span>
                        </div>

                        {/* 扣款日（僅正常訂閱顯示） */}
                        {isActive && !isFree && (
                            <div className="d-flex justify-content-between">
                                <span>{t('billing_date')}</span>
                                <span>{formatDate(next_billing_date)}</span>
                            </div>
                        )}

                        {/* 已取消續訂文字（非按鈕） */}
                        {isCancelled && (
                            <div className="text-end text-gray">
                                {t('subscription_cancelled')}
                            </div>
                        )}
                    </div>

                    {/* 按鈕區域 - 固定在底部 */}
                    <div className="mt-3 d-flex justify-content-end">
                        {/* 已到期或已取消 → 顯示續訂按鈕 */}
                        {(isExpired || isCancelled) && (
                            <button
                                className="btn btn-width btn-primary rounded-pill px-4"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRenewSubscription && onRenewSubscription(idol);
                                }}
                            >
                                {t('renew')}
                            </button>
                        )}

                        {/* 正常訂閱中 → 顯示取消訂閱按鈕 */}
                        {isActive && !isExpired && (
                            <button
                                className="btn btn-width btn-neutral rounded-pill px-4"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCancelSubscription && onCancelSubscription(idol);
                                }}
                            >
                                {t('cancel_subscription')}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default IdolCard;
